import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Settings, BookOpen, MapPin, FileText } from 'lucide-react';
import { QuestionnaireData } from '@/types/category';
import { useToast } from '@/hooks/use-toast';
import CategoryQuestions from '../CategoryQuestions';
import MassageInputs from '../MassageInputs';

interface SessionDetailsStepProps {
  formData: Partial<QuestionnaireData>;
  onUpdate: (updates: Partial<QuestionnaireData>) => void;
  categoryConfig: any;
  sessionType: 'individual' | 'group';
  categoryId: string;
  isPractitionerMode?: boolean;
}

export const SessionDetailsStep: React.FC<SessionDetailsStepProps> = ({
  formData,
  onUpdate,
  categoryConfig,
  sessionType,
  categoryId,
  isPractitionerMode = false
}) => {
  const { toast } = useToast();
  const [useBiography, setUseBiography] = React.useState(formData.usePersonalBiography ?? false);
  const MAX_ENV_CHARS = 200;

  const handleBiographyToggle = (checked: boolean) => {
    setUseBiography(checked);
    onUpdate({ usePersonalBiography: checked });
  };

  const handleEnvironmentChange = (value: string) => {
    if (value.length > MAX_ENV_CHARS) {
      const truncated = value.slice(0, MAX_ENV_CHARS);
      onUpdate({ environment: truncated });
      toast({
        title: 'Text truncated',
        description: `Limited to ${MAX_ENV_CHARS} characters to keep things concise.`,
      });
      return;
    }
    onUpdate({ environment: value });
  };

  // Get client name for biography label
  const clientName = formData.firstName || 'client';

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
          <Settings className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-1">Session Details</h3>
        <p className="text-sm text-muted-foreground">
          Customize your experience with specific preferences
        </p>
      </div>

      {/* Session Title */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Session Title (Optional)
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Give your session a memorable name for easy reference later
            </p>
            <Input
              placeholder="e.g., 'Morning Relaxation' or 'Sleep Journey'"
              value={formData.sessionTitle || ''}
              onChange={(e) => onUpdate({ sessionTitle: e.target.value })}
              maxLength={100}
            />
          </div>
        </CardContent>
      </Card>

      {/* Client Biography */}
      {sessionType === 'individual' && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="use-biography"
                checked={useBiography}
                onCheckedChange={handleBiographyToggle}
                className="mt-1"
              />
              <div className="space-y-1 flex-1">
                <Label
                  htmlFor="use-biography"
                  className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                >
                  <BookOpen className="h-4 w-4" />
                  Use {clientName}'s biography to personalize this session
                </Label>
                <p className="text-xs text-muted-foreground">
                  {clientName}'s saved biography helps create deeply personalized sessions tailored to their unique preferences, experiences, and goals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category-Specific Questions */}
      {categoryConfig && (
        <Card>
          <CardContent className="pt-6">
          <CategoryQuestions
            categoryConfig={categoryConfig}
            formData={formData as QuestionnaireData}
            setFormData={(updates) => onUpdate(updates)}
            sessionType={sessionType}
            isPractitionerMode={isPractitionerMode}
          />
          </CardContent>
        </Card>
      )}

      {/* Massage-Specific Inputs */}
      {categoryId === 'massage' && (
        <Card>
          <CardContent className="pt-6">
            <MassageInputs
              formData={formData as QuestionnaireData}
              setFormData={(updates) => onUpdate(updates)}
              categoryId={categoryId}
            />
          </CardContent>
        </Card>
      )}

      {/* Environment Personalization */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Current Environment (Optional)
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Describe where your {sessionType === 'group' ? 'group is' : 'client is'} for deeper immersion
            </p>
            <Textarea
              placeholder="e.g., 'In a quiet bedroom with soft lighting and a candle burning...'"
              value={formData.environment || ''}
              onChange={(e) => handleEnvironmentChange(e.target.value)}
              className="min-h-[80px] resize-none"
            />
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">
                Helps create more vivid imagery
              </span>
              <span className={`font-medium ${
                (formData.environment?.length || 0) > MAX_ENV_CHARS * 0.9
                  ? 'text-destructive'
                  : (formData.environment?.length || 0) > MAX_ENV_CHARS * 0.8
                  ? 'text-yellow-600 dark:text-yellow-500'
                  : 'text-green-600 dark:text-green-500'
              }`}>
                {formData.environment?.length || 0}/{MAX_ENV_CHARS}
              </span>
            </div>
            {(formData.environment?.length || 0) > MAX_ENV_CHARS * 0.9 && (
              <p className="text-xs text-destructive">
                💡 Consider keeping it brief for better results
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};